import {
  CSSelectOption,
  Constraint,
  ConstraintArg,
  GetSegmentsResponse,
  QuestionSearchFilters,
  SegmentSearchParams,
  SegmentSimple,
  SegmentSortTerm,
  SegmentVisibility,
  segmentSortTermValues,
  segmentVisibilityValues,
} from "types";
import { QuestionInputType, QuestionRoleType, SortTypes } from "consts";
import { areArraysEqual, isNullOrUndefOrEmpty } from "@civicscience/chops";
import { buildDefaultQuestionSearchFilterValues, defaultQuestionSearchTableFilterValues } from "utils/questions";

import { ApiDateFilter } from "types/dates";
import { ValueParser } from "utils/url/querystring";

type UpdateSegmentVisibilityProps = {
  segmentVisibilitySelected?: SegmentVisibility;
};

export const updateSegmentVisibility = ({
  segmentVisibilitySelected,
}: UpdateSegmentVisibilityProps): SegmentVisibility => {
  return isNullOrUndefOrEmpty(segmentVisibilitySelected) ? "" : segmentVisibilitySelected;
};

type CreateSegmentVisibilityOptionsProps = {
  segmentVisibility: SegmentVisibility;
};

export const createSegmentVisibilityOptions = ({ segmentVisibility = "" }: CreateSegmentVisibilityOptionsProps) => {
  return [
    { text: "All", value: "", checked: segmentVisibility === "", description: "All segments" },
    {
      text: "Syndicated",
      value: "syndicated",
      checked: segmentVisibility === "syndicated",
      description: "Syndicated: Predefined segments accessible to all clients and customers.",
    },
    {
      text: "Custom",
      value: "custom",
      checked: segmentVisibility === "custom",
      description: "Custom: Tailored segments designed and shared selectively within your account only.",
    },
    {
      text: "Shared",
      value: "shared",
      checked: segmentVisibility === "shared",
      description: "Shared: Segments that are shared with a selected group of accounts.",
    },
  ];
};

type VisibilityOrderForSegmentSortType = SegmentVisibility | "" | undefined;

type sortSegmentOptionsByVisibilityProps = {
  segmentOptions: SegmentSimple[];
  visibilityOrder?: VisibilityOrderForSegmentSortType[];
};
export const sortSegmentOptionsByVisibility = ({
  segmentOptions = [],
  visibilityOrder = [undefined, "syndicated", "custom", "shared"],
}: sortSegmentOptionsByVisibilityProps) =>
  [...segmentOptions].sort((a, b) => {
    const indexA = visibilityOrder.indexOf(a.visibility || undefined);
    const indexB = visibilityOrder.indexOf(b.visibility || undefined);
    return indexA - indexB;
  });

type GetFilteredSegmentOptionsByVisibilityProps = {
  segmentVisibility: SegmentVisibility;
  segmentOptions?: SegmentSimple[];
};

export const getFilteredSegmentOptionsByVisibility = ({
  segmentOptions = [],
  segmentVisibility = "",
}: GetFilteredSegmentOptionsByVisibilityProps) => {
  const sorted = sortSegmentOptionsByVisibility({ segmentOptions });

  // treat an empty list the same as "all"
  if (isNullOrUndefOrEmpty(segmentVisibility)) {
    return sorted;
  }

  // sort the items and then filter them based on visibility
  return sorted.filter((x) => x.visibility && segmentVisibility === x.visibility);
};

/**
 * Helper designed for the `select` callback of react-query's useQuery hook.
 * Returns a function that filters the segment options based on the provided `segmentVisibility`.
 * If there is no visibility, it will return the original list.
 *
 * This also allows for `customSegments` which will be prepended to the list of segments
 * but ONLY when there is NOT a visibility filter.
 * This is used for QuestionFilters which provide an "ALL" option in some cases.
 *
 * See also: `getFilteredSegmentOptionsByVisibility`.
 */
export const selectSegmentsByVisibility = (
  segmentVisibility: SegmentVisibility,
  customSegments: SegmentSimple[] = [],
) => {
  return (segmentOptions: SegmentSimple[]) =>
    segmentVisibility
      ? getFilteredSegmentOptionsByVisibility({
          segmentOptions,
          segmentVisibility,
        })
      : [...customSegments, ...segmentOptions];
};

/**
 * Determines if two ConstraintArg objects are equal.
 * This is used to prevent adding duplicate constraints to a segment.
 *
 * Comparison is based on:
 * - seg: uuid
 * - geo/att/zip: id and answers.v array
 */
export const areConstraintArgsEqual = (args1: ConstraintArg, args2: ConstraintArg): boolean => {
  if (args1.type !== args2.type) {
    return false;
  }

  switch (args1.type) {
    case "seg":
      return args1.uuid === args2.uuid;
    case "geo":
    case "att":
    case "zip":
      return args1.id === args2.id && areArraysEqual<string | number>(args1.answers?.v || [], args2.answers?.v || []);
  }
};

/**
 * Returns `true` if both constraints have the same type and the same args (in any order).
 */
export const areConstraintsEqual = (constraint1: Constraint, constraint2: Constraint): boolean => {
  if (constraint1.type !== constraint2.type || constraint1.args.length !== constraint2.args.length) {
    return false;
  }

  // Each arg in constraint1 must match a *different* arg in constraint2, so duplicates are counted correctly
  const unmatchedArgs = [...constraint2.args];
  return constraint1.args.every((arg1) => {
    const matchIndex = unmatchedArgs.findIndex((arg2) => areConstraintArgsEqual(arg1, arg2));
    if (matchIndex === -1) {
      return false;
    }
    unmatchedArgs.splice(matchIndex, 1);
    return true;
  });
};

/**
 * Parse a plain string into a SegmentVisibility.
 * If the input is not a valid SegmentVisibility, defaults to "" (all).
 */
const parseSegmentVisibility = (val: string | null | undefined): SegmentVisibility => {
  return segmentVisibilityValues.includes(val as SegmentVisibility) ? (val as SegmentVisibility) : "";
};

/**
 * Builds a segment visibility parser with a default value.
 */
export const segmentVisibilityParserWithDefault = (
  defaultValue: SegmentVisibility,
): ValueParser<SegmentVisibility, SegmentVisibility> => ({
  defaultValue,
  parser: parseSegmentVisibility,
});

/**
 * Parse a plain string into a SegmentSortTerm.
 * If the input is not a valid SegmentSortTerm, defaults to "created-desc".
 */
const parseSegmentSortTerm = (val: string | null | undefined): SegmentSortTerm => {
  return segmentSortTermValues.includes(val as SegmentSortTerm) ? (val as SegmentSortTerm) : "created-desc";
};

/**
 * Builds a segment sort term parser with a default value.
 */
export const segmentSortTermParserWithDefault = (
  defaultValue: SegmentSortTerm,
): ValueParser<SegmentSortTerm, SegmentSortTerm> => ({
  defaultValue,
  parser: parseSegmentSortTerm,
});

/**
 * The possible sort options for segments.
 */
export const segmentSortOptions: { text: string; value: SegmentSortTerm }[] = [
  { value: "name", text: "Name" },
  { value: "created-desc", text: "Newest first" },
  { value: "created-asc", text: "Oldest first" },
];

/**
 * Default values to use for segment search params (filters, sorting, pagination).
 */
export const defaultSegmentSearchParams: Readonly<SegmentSearchParams> = {
  query: "",
  visibility: "",
  favorites: false,
  sortTerm: "created-desc",
  page: 1,
  size: 25,
};

/**
 * Options for the Input Type filter in the Segment Constraint Question Search.
 */
export const inputTypeGroupOptionsSegment: CSSelectOption[] = [{ text: "Radio", value: QuestionInputType.Radio }];

/**
 * The "default" filter values to use for question constraint searches.
 */
export const buildQuestionConstraintSearchDefaultFilters = (isInternal: boolean): QuestionSearchFilters => ({
  ...buildDefaultQuestionSearchFilterValues(isInternal),
  ...defaultQuestionSearchTableFilterValues,
  sortTerm: SortTypes.RELEVANCE,
  inputType: [QuestionInputType.Radio],
  questionRole: [QuestionRoleType.Engagement, QuestionRoleType.Value, QuestionRoleType.Profile],
});

// Custom relative date filters are only supported when both time units are "days".
// Other units (weeks, months, quarters, years) are not supported for ad targeting.
const hasUnsupportedCustomRelativeDateFilter = (df: ApiDateFilter): boolean => {
  const crd = df.customRelativeDateFilter;
  if (crd === null) return false;
  return crd.periodUnit !== "days" || crd.relativeUnit !== "days";
};

const getUnsupportedDateFilterMessage = (dateFilter: ApiDateFilter | undefined): string | null => {
  if (!dateFilter) return null;
  if (hasUnsupportedCustomRelativeDateFilter(dateFilter)) {
    return "This constraint uses a custom relative date filter with non-day units, which is not supported for ad targeting.";
  }
  return null;
};

/**
 * Returns a warning message if the given constraint has a configuration that may produce
 * unexpected results for ad targeting, or null if the constraint is fully supported.
 *
 * Warning conditions:
 * - Any arg uses a custom relative date filter with non-day time units
 */
export const getConstraintWarning = (constraint: Constraint): string | null => {
  for (const arg of constraint.args) {
    const message = getUnsupportedDateFilterMessage(arg.dateFilter);
    if (message) return message;
  }
  return null;
};

/**
 * Filters out a segment by ID from the response and adjusts pagination metadata accordingly.
 * This is useful when displaying segments in a constraint search where you need to exclude
 * the current segment to prevent circular references.
 *
 * NOTE: We could move this logic into the backend in the future if needed.
 */
export const filterSegmentByIdWithPagination = (data: GetSegmentsResponse, segmentId: string): GetSegmentsResponse => {
  const filteredResults = data.results?.filter((segment) => segment.uuid !== segmentId) ?? [];
  const wasFiltered = (data.results?.length ?? 0) > filteredResults.length;

  return {
    ...data,
    results: filteredResults,
    // Adjust total count if we filtered something out
    total: wasFiltered ? data.total - 1 : data.total,
    // Recalculate pageTotal based on new total
    pageTotal: wasFiltered ? Math.ceil((data.total - 1) / data.size) : data.pageTotal,
  };
};
